import Button from '@pinecast/common/Button';
import Callout from '@pinecast/common/Callout';
import EmptyState from '@pinecast/common/EmptyState';
import Form from '@pinecast/common/Form';
import Label from '@pinecast/common/Label';
import Section from '@pinecast/common/Section';
import {P} from '@pinecast/common/Text';
import TextArea from '@pinecast/common/TextArea';
import {useManagedMutation} from '@pinecast/gql';
import type {
  LeaveSubscriberFeedback,
  LeaveSubscriberFeedbackVariables,
} from '@pinecast/gql/__generated_tj__/LeaveSubscriberFeedback';
import type {RecurringDonation_tipJarSubscription} from '@pinecast/gql/__generated_tj__/RecurringDonation';
import {defineMessages, FormattedMessage} from '@pinecast/i18n';
import gql from 'graphql-tag';
import * as React from 'react';

const messages = defineMessages({
  success: {
    id: 'tj-spa.RecurringDonationPageFeedback.message.success',
    description: 'Success message when leaving a comment for a podcast',
    defaultMessage: 'We successfully delivered your comment.',
  },
  error: {
    id: 'tj-spa.RecurringDonationPageFeedback.message.error',
    description: 'Error message when leaving a comment for a podcast',
    defaultMessage: "We weren't able to deliver your comment. Try again later.",
  },
  copy: {
    id: 'tj-spa.RecurringDonationPageFeedback.copy',
    description: 'Explanation of submitting a comment to a podcaster',
    defaultMessage:
      'As part of your subscription, you can send messages to the owner of this podcast. Your message, along with your email address, will be shared.',
  },
  label: {
    id: 'tj-spa.RecurringDonationPageFeedback.message.label',
    description: 'Label for field to leave a comment for a podcast',
    defaultMessage: 'Your message',
  },
  submit: {
    id: 'tj-spa.RecurringDonationPageFeedback.submit',
    description: 'Button to leave a comment for a podcast',
    defaultMessage: 'Submit comment',
  },

  inactive: {
    id: 'tj-spa.RecurringDonationPageFeedback.inactive',
    description: 'Title indicating the the comment box is not active',
    defaultMessage: 'This show is not accepting comments',
  },
  inactiveCopy: {
    id: 'tj-spa.RecurringDonationPageFeedback.inactive.copy',
    description: 'Copy indicating the the comment box is not active',
    defaultMessage:
      'The owner of this podcast has chosen not to accept comments right now.',
  },
});

const LEAVE_FEEDBACK = gql`
  mutation LeaveSubscriberFeedback($slug: ID!, $message: String!) {
    leaveSubscriberFeedback(podcastSlug: $slug, message: $message)
  }
`;

const RecurringDonationPageFeedback = ({
  subscription,
}: {
  subscription: RecurringDonation_tipJarSubscription;
}) => {
  const leaveFeedback = useManagedMutation<
    LeaveSubscriberFeedback,
    LeaveSubscriberFeedbackVariables
  >(LEAVE_FEEDBACK);
  const [message, setMessage] = React.useState('');
  if (!subscription.podcast.feedbackActive) {
    return (
      <Section>
        <EmptyState
          title={<FormattedMessage {...messages.inactive} />}
          copy={<FormattedMessage {...messages.inactiveCopy} />}
        />
      </Section>
    );
  }
  return (
    <Section>
      {leaveFeedback.success ? (
        <Callout type="positive">
          <FormattedMessage {...messages.success} />
        </Callout>
      ) : leaveFeedback.error ? (
        <Callout type="negative">
          <FormattedMessage {...messages.error} />
        </Callout>
      ) : (
        <P>
          <FormattedMessage {...messages.copy} />
        </P>
      )}
      <Form
        onSubmit={() => {
          leaveFeedback.action({
            variables: {message, slug: subscription.podcast.slug},
          });
        }}
      >
        <Label text={<FormattedMessage {...messages.label} />}>
          <TextArea
            value={message}
            onChange={setMessage}
            disabled={leaveFeedback.pending || leaveFeedback.success}
          />
        </Label>
        <Button
          pending={leaveFeedback.pending}
          disabled={leaveFeedback.success}
          type="submit"
        >
          <FormattedMessage {...messages.submit} />
        </Button>
      </Form>
    </Section>
  );
};

export default RecurringDonationPageFeedback;
